import React, { useEffect, useState } from "react";
import CrossAttachmentAssessment from "../../Create_Assessment/Attachment/CrossAttachmentAssessment";
import { Box, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

/* ⭐ Confidence color helper */
const confidenceColor = (c) => {
  if (c == null) return { bg: "#f3f4f6", color: "#6b7280", label: "—" };
  if (c >= 0.8) return { bg: "#dcfce7", color: "#15803d", label: "High" };
  if (c >= 0.5) return { bg: "#fef3c7", color: "#b45309", label: "Medium" };
  return { bg: "#fee2e2", color: "#b91c1c", label: "Low" };
};

const UploadQuestion = ({
  response,
  questionNumber,
  setResponseAnswers,
  studentResults,
  isAdaptive,
  isAutoEvalMode = false,
  isReadOnly = false,
}) => {
  if (isAdaptive) {
    response = response?.question_response;
  }

  const [inputValue, setInputValue] = useState({
    remark: "",
    score: null,
  });

  const { remark, score } = inputValue;

  const matchResultWithResponse = () => {
    let result = studentResults.find(
      (result) => result?.question_response?.id === response?.id,
    );
    return result;
  };

  useEffect(() => {
    !studentResults?.length > 0 &&
      setInputValue({
        remark: response?.result?.remark,
        score: response?.result?.score ? response?.result?.score : "",
      });
    const resultInitialization = () => {
      setResponseAnswers((prevState) =>
        prevState.map((responseData) =>
          responseData.id === response.id
            ? {
                ...responseData,
                result: {
                  ...responseData.result,
                  questionResponse: response.id,
                  correctScore: response?.question?.correct_score,
                  score: null,
                },
              }
            : responseData,
        ),
      );
    };
    (!response?.result?.questionResponse || !response?.result?.correctScore) &&
      !studentResults?.length > 0 &&
      resultInitialization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  /* ⭐ Auto-eval ke liye local state sync */
  useEffect(() => {
    if (isAutoEvalMode && response?.result) {
      const s = response.result.score;
      setInputValue({
        remark: response.result.remark ?? "",
        score: s === null || s === undefined ? "" : s,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoEvalMode, response?.result?.score, response?.result?.remark]);

  const handleSubmitRemark = (e) => {
    setResponseAnswers((prevState) =>
      prevState.map((responseData) =>
        responseData.id === response.id
          ? {
              ...responseData,
              result: { ...responseData.result, remark: e.target.value },
            }
          : responseData,
      ),
    );
  };

  const handleSubmitScore = (e) => {
    const newScore = e.target.value !== "" ? Number(e.target.value) : null;
    setResponseAnswers((prevState) =>
      prevState.map((responseData) =>
        responseData.id === response.id
          ? {
              ...responseData,
              result: { ...responseData.result, score: newScore },
            }
          : responseData,
      ),
    );
  };

  /* ⭐ Auto-eval inline handlers */
  const handleAutoScoreChange = (e) => {
    if (isReadOnly) return;
    const raw = e.target.value;
    if (!/^\d*$/.test(raw)) return;
    const numeric = raw === "" ? null : Number(raw);
    if (numeric !== null && numeric > (response?.autoMeta?.max_marks ?? 0))
      return;

    setInputValue((prev) => ({ ...prev, score: raw }));

    setResponseAnswers((prevState) =>
      prevState.map((rd) =>
        rd.id === response.id
          ? { ...rd, result: { ...rd.result, score: numeric } }
          : rd,
      ),
    );
  };

  const handleAutoRemarkChange = (e) => {
    if (isReadOnly) return;
    const val = e.target.value;
    if (!/^[a-zA-Z0-9 ]*$/.test(val)) return;

    setInputValue((prev) => ({ ...prev, remark: val }));

    setResponseAnswers((prevState) =>
      prevState.map((rd) =>
        rd.id === response.id
          ? { ...rd, result: { ...rd.result, remark: val } }
          : rd,
      ),
    );
  };

  const autoMeta = response?.autoMeta;
  const showAutoPanel = isAutoEvalMode && autoMeta;

  // ⭐ AI grading basis data
  const criteriaList = autoMeta?.evaluation_criteria ?? [];
  const explanationText = autoMeta?.evaluation_explanation ?? "";
  const hasGradingBasis =
    explanationText.trim().length > 0 || criteriaList.length > 0;

  // ⭐ Per-question editable — review me SAB editable, completed me sab locked
  const isMarksEditable = !isReadOnly;
  const isRemarkEditable = !isReadOnly;

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1,
        p: 3,
        borderRadius: 3,
        border: "1px solid #f1c27d",
        backgroundColor: "#ffffff",
      }}
    >
      {/* QUESTION HEADER */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={2}>
        <Typography fontWeight={700} color="#8D4B28">
          Q{questionNumber + 1}.
        </Typography>

        <Typography fontWeight={600} lineHeight={1.6}>
          {response?.question?.title?.split("\n").map((line, index) => (
            <span key={index}>
              {index !== 0 && <br />}
              {line}
            </span>
          ))}
        </Typography>
      </Stack>

      {/* QUESTION ATTACHMENTS */}
      {response?.question?.attachments?.length > 0 && (
        <Stack direction="row" spacing={2} mb={2} ml={4}>
          {response.question.attachments.map((attachment, index) => (
            <Box key={index} maxWidth={220}>
              <CrossAttachmentAssessment attachment={attachment} />
            </Box>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />

      {/* STUDENT ANSWER */}
      <Typography fontWeight={600} sx={{ color: "#001A6E", mb: 1 }}>
        Student Answer
      </Typography>

      {response?.description ? (
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
          }}
        >
          <Typography>{response.description}</Typography>
        </Box>
      ) : (
        <Typography color="text.secondary">No Answer</Typography>
      )}

      {/* ============================================================
          ⭐ AUTO-EVAL PANEL — Marks prominent, remark compact
      ============================================================ */}
      {showAutoPanel && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            border: "1px solid #e5e7eb",
            bgcolor: "#f9fafb",
          }}
        >
          {/* Top row — confidence + status chips */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            mb={2}
            flexWrap="wrap"
            useFlexGap
          >
            <AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
            <Typography fontWeight={700} fontSize={13} color="#4c1d95">
              AI Suggestion
            </Typography>

            {autoMeta.confidence != null ? (
              (() => {
                const cfg = confidenceColor(autoMeta.confidence);
                return (
                  <Chip
                    label={`Confidence ${(autoMeta.confidence * 100).toFixed(
                      0,
                    )}% · ${cfg.label}`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: cfg.bg,
                      color: cfg.color,
                    }}
                  />
                );
              })()
            ) : (
              <Chip
                label="Marks pending"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: "#fef3c7",
                  color: "#b45309",
                }}
              />
            )}

            {autoMeta.grading_status === "auto_graded" && !isReadOnly && (
              <Chip
                label="AI Graded · Editable"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: "#e0e7ff",
                  color: "#3730a3",
                }}
              />
            )}

            {autoMeta.grading_status === "manually_graded" && !isReadOnly && (
              <Chip
                label="Manually Graded · Editable"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: "#dcfce7",
                  color: "#15803d",
                }}
              />
            )}

            {autoMeta.grading_status === "manually_graded" && isReadOnly && (
              <Chip
                label="Manually Graded · Final"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: "#dcfce7",
                  color: "#15803d",
                }}
              />
            )}

            {autoMeta.grading_status === "manual_required" && !isReadOnly && (
              <Chip
                label="Needs manual marks"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: "#fee2e2",
                  color: "#b91c1c",
                }}
              />
            )}

            {isReadOnly && (
              <Chip
                label="Read only"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: "#f3f4f6",
                  color: "#6b7280",
                }}
              />
            )}
          </Stack>

          {/* Main row — Marks BIG + Remark small */}
          <Stack
            direction="row"
            spacing={2.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            {/* ⭐ Marks — prominent, big font, highlighted */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: isMarksEditable ? "#fff" : "#f3f4f6",
                border: "2px solid",
                borderColor: isMarksEditable ? "#7c3aed" : "#e5e7eb",
                flexShrink: 0,
              }}
            >
              <Typography
                fontSize={12}
                fontWeight={700}
                color="#6b7280"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Marks
              </Typography>

              <input
                value={score ?? ""}
                onChange={handleAutoScoreChange}
                type="text"
                placeholder="—"
                disabled={!isMarksEditable}
                readOnly={!isMarksEditable}
                style={{
                  width: 70,
                  height: 44,
                  fontSize: 26,
                  fontWeight: 700,
                  color: isMarksEditable ? "#111827" : "#6b7280",
                  textAlign: "center",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  cursor: isMarksEditable ? "text" : "not-allowed",
                  fontFamily: "inherit",
                }}
              />

              <Typography
                fontSize={22}
                fontWeight={700}
                color="#9ca3af"
                sx={{ lineHeight: 1 }}
              >
                /
              </Typography>

              <Typography
                fontSize={22}
                fontWeight={700}
                color="#4c1d95"
                sx={{ lineHeight: 1 }}
              >
                {autoMeta.max_marks}
              </Typography>
            </Box>

            {/* ⭐ Remark — small, compact */}
            <Box
              sx={{
                flex: "1 1 200px",
                minWidth: 180,
              }}
            >
              <input
                value={remark ?? ""}
                onChange={handleAutoRemarkChange}
                maxLength={250}
                placeholder={isRemarkEditable ? "Teacher remark…" : "—"}
                disabled={!isRemarkEditable}
                readOnly={!isRemarkEditable}
                style={{
                  width: "100%",
                  height: 36,
                  padding: "0 10px",
                  fontSize: 12,
                  borderRadius: 6,
                  border: "1px solid #e5e7eb",
                  outline: "none",
                  background: isRemarkEditable ? "#fff" : "#f3f4f6",
                  color: "#374151",
                  cursor: isRemarkEditable ? "text" : "not-allowed",
                  fontFamily: "inherit",
                }}
              />
            </Box>
          </Stack>

          {/* ⭐⭐ AI GRADING BASIS — criteria + explanation */}
          {hasGradingBasis && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: "#f5f3ff",
                border: "1px dashed #c4b5fd",
              }}
            >
              <Typography
                fontSize={11}
                fontWeight={700}
                color="#4c1d95"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  mb: 0.75,
                }}
              >
                AI Grading Basis
              </Typography>

              {/* Explanation */}
              {explanationText && (
                <Typography
                  fontSize={12}
                  color="#374151"
                  mb={criteriaList.length > 0 ? 1.25 : 0}
                  lineHeight={1.6}
                >
                  {explanationText}
                </Typography>
              )}

              {/* Criteria chips */}
              {criteriaList.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {criteriaList.map((c, i) => (
                    <Chip
                      key={i}
                      size="small"
                      label={`${c.criterion} · ${c.marks} mark${
                        c.marks === 1 ? "" : "s"
                      }`}
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: "#ede9fe",
                        color: "#5b21b6",
                        border: "1px solid #ddd6fe",
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {/* Footer note */}
          <Typography
            fontSize={11}
            color={isMarksEditable ? "red" : "#6b7280"}
            mt={1.25}
          >
            {isReadOnly
              ? "This assessment is completed. Marks are final."
              : autoMeta.hasObtainedMarks
                ? `NOTE: Marks cannot exceed ${autoMeta.max_marks}`
                : `AI could not assign marks. Please enter manually (max ${autoMeta.max_marks}).`}
          </Typography>
        </Box>
      )}

      {/* TEACHER REVIEW (non-auto) */}
      {!isAdaptive && !isAutoEvalMode && (
        <>
          <Typography fontWeight={600} mb={1}>
            Teacher Comment
          </Typography>

          <textarea
            onBlur={handleSubmitRemark}
            onChange={(e) => {
              const newValue = e.target.value;
              if (/^[a-zA-Z0-9 ]*$/.test(newValue)) {
                setInputValue((prev) => ({
                  ...prev,
                  remark: newValue,
                }));
              }
            }}
            value={
              studentResults?.length > 0
                ? matchResultWithResponse()?.comment
                : remark
            }
            readOnly={studentResults?.length > 0}
            maxLength={250}
            className="remark_textfield_box_hw"
            placeholder="Type Remark here..."
          />

          <Stack direction="row" spacing={2} alignItems="center" mt={3}>
            <Typography>
              Allot marks out of {response?.question?.correct_score}
            </Typography>

            <input
              onBlur={(e) => {
                if (e.target.value <= response?.question?.correct_score) {
                  handleSubmitScore(e);
                }
              }}
              onChange={(e) => {
                const newValue = e.target.value;
                if (/^\d*$/.test(newValue)) {
                  const numericValue = Number(newValue);
                  if (numericValue <= response?.question?.correct_score) {
                    setInputValue((prev) => ({
                      ...prev,
                      score: numericValue,
                    }));
                  }
                }
              }}
              value={
                studentResults?.length > 0
                  ? matchResultWithResponse()?.obtained_marks
                  : score
              }
              readOnly={studentResults?.length > 0}
              className="score_box_hw px-2"
              type="text"
              placeholder="0 Marks"
            />
          </Stack>

          <Typography fontSize={12} color="red" mt={1}>
            NOTE: You cannot enter more than correct marks
          </Typography>
        </>
      )}

      {/* CORRECT ANSWER */}
      {!isAutoEvalMode && response?.question?.correct_answers?.length > 0 && (
        <Box mt={4}>
          <Typography fontWeight={600} mb={2}>
            Correct Answer
          </Typography>

          <Stack spacing={2}>
            {response.question.correct_answers.map((cAnswer, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #16a34a",
                  backgroundColor: "#f0fdf4",
                }}
              >
                {cAnswer?.reason_for_answer?.explanation && (
                  <Typography>
                    {cAnswer.reason_for_answer.explanation}
                  </Typography>
                )}

                {cAnswer?.reason_for_answer?.attachment && (
                  <Box mt={1}>
                    <CrossAttachmentAssessment
                      attachment={cAnswer.reason_for_answer.attachment}
                    />
                  </Box>
                )}
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

export default UploadQuestion;
